import React, { ComponentType, useMemo, useRef } from 'react';

import { Button } from 'antd';
import classNames from 'classnames';
import L from 'leaflet';
import {
  Circle as LeafletCircle,
  Marker as LeafletMarker,
  Tooltip as LeafletTooltip,
} from 'react-leaflet';

import { BASEMAP } from '@src/components/Maps/constants';
import Map from '@src/components/Maps/Map';

import { FitBounds } from './components/FitBounds';
import { MapMarkerWithPopup } from './components/MapMarkerWithPopup';
import { MapResizer } from './components/MapResizer';
import {
  TLocationPosition,
  TLocationsMapMarkerVariant,
  TLocationWithPosition,
  TLocationsMapProps,
  TMapItem,
  TMarkerItem,
} from './LocationsMap.interfaces';
import styles from './styles.module.less';
import { getGeofenceFromMapItem, getGeofencePathOptions } from './utils/geofence';
import { createLocationsMapMarkerIcon } from './utils/locationsMapMarkerIcon';
import {
  getLocationPosition,
  isValidLatLngTuple,
  parseCoordinatePair,
} from './utils/parseCoordinates';

const Circle = LeafletCircle as unknown as ComponentType<{
  center: [number, number];
  radius: number;
  pathOptions?: L.PathOptions;
  interactive?: boolean;
}>;

const Marker = LeafletMarker as unknown as ComponentType<{
  position: [number, number];
  icon?: L.DivIcon;
  children?: React.ReactNode;
}>;

const Tooltip = LeafletTooltip as unknown as ComponentType<{
  children?: React.ReactNode;
  direction?: string;
  offset?: [number, number];
}>;

delete (L.Icon.Default.prototype as L.Icon.Default & { _getIconUrl?: unknown })._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

const DEFAULT_CENTER: [number, number] = [-33.4489, -70.6693];
const DEFAULT_ZOOM = 13;
const SINGLE_MARKER_ZOOM = 16;

export { getLocationPosition, parseCoordinatePair } from './utils/parseCoordinates';

const resolveMarkerPosition = <T extends TMapItem>(
  item: T,
  getPosition: (item: T) => TLocationPosition | null,
): TLocationPosition | null => {
  const resolved = getPosition(item);

  return (
    parseCoordinatePair(resolved) ?? getLocationPosition(item as unknown as TLocationWithPosition)
  );
};

const LocationsMap = <T extends TMapItem>({
  items = [],
  getPosition = getLocationPosition as (item: T) => TLocationPosition | null,
  renderPopup = null,
  getPopupText = null,
  getActionLabel = null,
  onItemAction = null,
  visible = true,
  height = '400px',
  mapMinHeight,
  className = '',
  mapClassName = '',
  emptyMessage = null,
  loading = false,
  basemap = BASEMAP.CARTO_LIGHT,
  popupTrigger = 'click',
  isItemSelected = null,
  getMarkerVariant = null,
  getMarkerLabel = null,
  getActionVariant = null,
  popupLayout = 'default',
  scrollWheelZoom = true,
  interactive = true,
}: TLocationsMapProps<T>) => {
  const mapRef = useRef<L.Map | null>(null);
  const markerIconsRef = useRef<Record<string, L.DivIcon>>({});

  const getMarkerIcon = (item: T) => {
    const variant: TLocationsMapMarkerVariant =
      getMarkerVariant?.(item) ?? (isItemSelected?.(item) ? 'selected' : 'default');
    const label = getMarkerLabel?.(item);
    const cacheKey = `${variant}|${label ?? ''}`;

    const cachedIcon = markerIconsRef.current[cacheKey];
    if (cachedIcon) {
      return cachedIcon;
    }

    const icon = createLocationsMapMarkerIcon({ variant, label });
    markerIconsRef.current[cacheKey] = icon;

    return icon;
  };

  const markers = useMemo((): TMarkerItem<T>[] => {
    return items.reduce<TMarkerItem<T>[]>((acc, item) => {
      const position = resolveMarkerPosition(item, getPosition);
      if (!position) return acc;

      acc.push({ id: item.id, item, position: [position.lat, position.lng] });
      return acc;
    }, []);
  }, [getPosition, items]);

  const geofenceCircles = useMemo(
    () =>
      markers.flatMap(marker => {
        const geofence = getGeofenceFromMapItem(marker.item);
        if (!geofence) return [];
        return [
          {
            id: marker.id,
            position: marker.position,
            radius: geofence.meters,
            outside: geofence.outside,
          },
        ];
      }),
    [markers],
  );

  const mapCenter = useMemo((): [number, number] => {
    const firstPosition = markers[0]?.position;
    if (firstPosition && isValidLatLngTuple(firstPosition)) {
      return firstPosition;
    }

    return DEFAULT_CENTER;
  }, [markers]);

  const mapZoom = markers.length === 1 ? SINGLE_MARKER_ZOOM : DEFAULT_ZOOM;
  const isHoverPopup = popupTrigger === 'hover';

  const renderMarkerPopup = (marker: TMarkerItem<T>, closePopup: () => void) => {
    if (renderPopup) return renderPopup(marker.item);

    const popupText = getPopupText?.(marker.item);
    const actionLabel = getActionLabel?.(marker.item);
    const hasAction = Boolean(onItemAction && actionLabel);

    if (!popupText && !hasAction) return null;

    const actionVariant = getActionVariant?.(marker.item) ?? 'primary';
    const isDangerAction = actionVariant === 'danger';
    const useCardLayout = popupLayout === 'card' && popupTrigger === 'click';

    const handleActionClick = (event: React.MouseEvent<HTMLElement>) => {
      event.stopPropagation();
      onItemAction?.(marker.item);
      closePopup();
    };

    if (useCardLayout) {
      return (
        <div className={styles.popupCard}>
          {popupText && <div className={styles.popupTitle}>{popupText}</div>}
          {hasAction && onItemAction && (
            <>
              <div className={styles.popupDivider} />
              <div className={styles.popupActionWrapper}>
                <Button
                  type="primary"
                  danger={isDangerAction}
                  className={styles.popupActionButton}
                  onClick={handleActionClick}
                >
                  {actionLabel}
                </Button>
              </div>
            </>
          )}
        </div>
      );
    }

    return (
      <div className={styles.popupContent}>
        {popupText && <div className={styles.popupText}>{popupText}</div>}
        {hasAction && onItemAction && (
          <Button
            type="primary"
            danger={isDangerAction}
            size="small"
            className={styles.popupAction}
            onClick={handleActionClick}
          >
            {actionLabel}
          </Button>
        )}
      </div>
    );
  };

  return (
    <div
      className={classNames(
        styles.container,
        popupLayout === 'card' && styles.containerCardPopup,
        className,
      )}
      style={{ minHeight: height }}
    >
      <Map
        center={mapCenter}
        zoom={mapZoom}
        mapRef={mapRef}
        loading={loading}
        basemap={basemap}
        scrollWheelZoom={interactive && scrollWheelZoom}
        dragging={interactive}
        zoomControl={interactive}
        doubleClickZoom={interactive}
        touchZoom={interactive}
        boxZoom={interactive}
        keyboard={interactive}
        className={classNames(styles.map, mapClassName)}
        style={{ height, ...(mapMinHeight ? { minHeight: mapMinHeight } : {}) }}
      >
        <MapResizer visible={visible} />
        {markers.length > 0 && (
          <FitBounds
            positions={markers.map(marker => marker.position)}
            zoom={mapZoom}
            circles={geofenceCircles.map(circle => ({
              position: circle.position,
              radius: circle.radius,
            }))}
          />
        )}
        {geofenceCircles.map(circle => (
          <Circle
            key={`geofence-${circle.id}`}
            center={circle.position}
            radius={circle.radius}
            interactive={false}
            pathOptions={getGeofencePathOptions(circle.outside)}
          />
        ))}
        {markers.map(marker => {
          const markerIcon = getMarkerIcon(marker.item);
          const popupContent = renderMarkerPopup(marker, () => {});

          if (!popupContent) {
            return <Marker key={marker.id} position={marker.position} icon={markerIcon} />;
          }

          if (isHoverPopup) {
            return (
              <Marker key={marker.id} position={marker.position} icon={markerIcon}>
                <Tooltip direction="top">{popupContent}</Tooltip>
              </Marker>
            );
          }

          return (
            <MapMarkerWithPopup key={marker.id} position={marker.position} icon={markerIcon}>
              {closePopup => renderMarkerPopup(marker, closePopup)}
            </MapMarkerWithPopup>
          );
        })}
      </Map>
      {!markers.length && emptyMessage && <div className={styles.emptyState}>{emptyMessage}</div>}
    </div>
  );
};

export default LocationsMap;
